// ---------------------------------------------------------------------------
// Route layouts
// ---------------------------------------------------------------------------

import { Navigate, useLocation } from 'react-router-dom';

import { AppShell } from '@/components/layout/AppShell';
import AuthGuard from '@/features/auth/components/AuthGuard';
import { useSyncEngine } from '@/hooks/useSyncEngine';
import { selectUser, useAuthStore } from '@/store/auth.store';
import SuspenseOutlet from './SuspenseOutlet';

/**
 * Authenticated layout — wraps all routes that require login.
 * Includes the AppShell (bottom nav, top bar) and auth guard.
 *
 * The useSyncEngine() hook initializes the offline database and sync engine
 * when the user is authenticated, and tears them down on logout. This is the
 * single place where the sync lifecycle is managed.
 */
export default function AuthenticatedLayout() {
  // Initialize offline sync engine (SQLite + mutation queue + periodic sync).
  // This is a no-op if already initialized for the current user.
  useSyncEngine();
  const user = useAuthStore(selectUser);
  const { pathname } = useLocation();

  // Someone new lands on Home after signing up or in; the welcome steps come
  // first. Only from Home, so a profile opened from the steps is not bounced
  // back. Strictly null: a user saved before the field existed lacks it.
  const welcome = pathname === '/' && user?.onboardedAt === null;

  return (
    <AuthGuard>
      {welcome ? (
        <Navigate to="/welcome" replace />
      ) : (
        <AppShell>
          <SuspenseOutlet />
        </AppShell>
      )}
    </AuthGuard>
  );
}
